import type { EventPageConfirmation, EventPageKnown, EventPageSlots, EventPageView, ResolvedAddress } from "@vaulted/shared";
import { EVENT_PAGE_FORMS_ACK, MINOR_NOTICE, ageOn, eventPageDepositAck, eventPageTextConsent, formatCents } from "@vaulted/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type CSSProperties, type ReactNode, useEffect, useMemo, useState } from "react";
import { trackingCookies, useMarketingTags } from "../marketing";
import { errorMessage, get, post } from "../api";
import { AddressField, type AddressLookup } from "../components/AddressField";
import { StripeCheckout } from "../components/StripeCheckout";
import { time12 } from "../components/AppointmentDialog";

/** Built-in fonts beyond the app's own two, loaded only when an event page uses them. */
const FONT_LOADERS: Record<string, () => Promise<unknown>> = {
  "Cormorant Garamond": () => Promise.all([import("@fontsource/cormorant-garamond/500.css"), import("@fontsource/cormorant-garamond/600.css")]),
  "Playfair Display": () => Promise.all([import("@fontsource/playfair-display/400.css"), import("@fontsource/playfair-display/600.css")]),
  Montserrat: () => Promise.all([import("@fontsource/montserrat/400.css"), import("@fontsource/montserrat/600.css")]),
  Lato: () => Promise.all([import("@fontsource/lato/400.css"), import("@fontsource/lato/700.css")]),
};

/** Default look: the NOUN game-day page. */
const DEFAULTS = { background: "#EFE9E3", card: "#F7F4F0", text: "#2B2523", heading: "#2B2523", button: "#1F1A1C" };

const todayLocal = () => new Date().toLocaleDateString("en-CA");
/** "10/05/1990" or "1990-10-05" → "1990-10-05" (null when it isn't a date yet). */
function isoFromTyped(s: string): string | null {
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (!m) return null;
  const iso = `${m[3]}-${m[1]!.padStart(2, "0")}-${m[2]!.padStart(2, "0")}`;
  return Number.isNaN(Date.parse(`${iso}T12:00:00Z`)) ? null : iso;
}
/** Types the slashes in a birthday as they go: 10051990 → 10/05/1990. */
const maskDob = (s: string) => {
  const d = s.replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join("/");
};
const maskPhone = (s: string) => {
  const d = s.replace(/\D/g, "").replace(/^1/, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};
const money = (c: number) => formatCents(c);
const monthDay = (local: string) => {
  const d = new Date(`${local.slice(0, 10)}T12:00:00Z`);
  return { month: d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }), day: d.getUTCDate(), weekday: d.toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" }) };
};

/** The event's own booking page: /<event-slug>. */
export function EventBookingPage({ slug: eventSlug }: { slug: string }) {
  const base = `/api/e/${eventSlug}`;
  const page = useQuery({ queryKey: ["event-page", eventSlug], queryFn: () => get<EventPageView>(base), retry: false });
  const e = page.data;
  useMarketingTags(e?.tracking);
  useEffect(() => {
    if (e) document.title = e.name;
  }, [e]);

  if (page.isLoading) return <div className="min-h-screen" style={{ backgroundColor: DEFAULTS.background }} />;
  if (!e)
    return (
      <div className="flex min-h-screen items-center justify-center px-4 text-center" style={{ backgroundColor: DEFAULTS.background, color: DEFAULTS.text }}>
        <p>We couldn't find that page. Check the link and try again.</p>
      </div>
    );
  return <EventShell e={e}>{e.open ? <BookingForm e={e} base={base} /> : <p className="py-10 text-center">Booking for this event isn't open yet. Check back soon.</p>}</EventShell>;
}

/**
 * An event's look around a page: its colors and fonts, the hero (photo, logo, headline), its edges,
 * and the footer. The booking page and the event's iPad both use it; `headline` replaces the hero's
 * own headline and intro (the iPad passes "": just the logo and photo).
 */
export function EventShell({ e, headline, children }: { e: EventPageView; headline?: string; children: ReactNode }) {
  const colors = { ...DEFAULTS, ...Object.fromEntries(Object.entries(e.colors ?? {}).filter(([, v]) => v)) };
  const heading = e.headingFont?.family ?? "Marcellus";
  const body = e.bodyFont?.family ?? "Manrope";
  useEffect(() => {
    for (const f of [e.headingFont, e.bodyFont]) if (f && !f.url) void FONT_LOADERS[f.family]?.();
  }, [e.headingFont, e.bodyFont]);
  const faces = [e.headingFont, e.bodyFont]
    .filter((f): f is { family: string; url: string } => !!f?.url)
    .map((f) => `@font-face{font-family:${JSON.stringify(f.family)};src:url(${JSON.stringify(f.url)});font-display:swap}`)
    .join("");
  const style = {
    "--ev-bg": colors.background,
    "--ev-card": colors.card,
    "--ev-text": colors.text,
    "--ev-heading": colors.heading,
    "--ev-button": colors.button,
    "--ev-heading-font": `${JSON.stringify(heading)}, Georgia, serif`,
    backgroundColor: "var(--ev-bg)",
    color: "var(--ev-text)",
    fontFamily: `${JSON.stringify(body)}, ui-sans-serif, system-ui, sans-serif`,
  } as CSSProperties;
  return (
    <div className="flex min-h-screen flex-col" style={style}>
      {faces && <style>{faces}</style>}
      <Hero e={headline === undefined ? e : { ...e, headline, intro: "" }} />
      <Edge e={e} />
      <main className="mx-auto w-full max-w-[600px] flex-1 px-4 py-8">{children}</main>
      <Edge e={e} footer />
      <footer className="px-4 py-8 text-center text-xs opacity-80">
        {(e.venueName || e.venueAddress) && <p>{[e.venueName, e.venueAddress].filter(Boolean).join(", ")}</p>}
        <p className="mt-1">Services provided by {e.clinicName}</p>
      </footer>
    </div>
  );
}

function Hero({ e }: { e: EventPageView }) {
  const focus = e.heroFocus ?? { x: 50, y: 50 };
  return (
    <header className="relative isolate overflow-hidden text-center text-white">
      {e.heroUrl && <img src={e.heroUrl} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" style={{ objectPosition: `${focus.x}% ${focus.y}%` }} />}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/45 via-black/45 to-black/75" />
      {/* Fade: the photo's bottom melts into the page; the text sits above it. */}
      {e.heroEdge === "fade" && <div aria-hidden className="absolute inset-x-0 bottom-0 -z-10 h-28 bg-gradient-to-b from-transparent to-[var(--ev-bg)]" />}
      <div className={`mx-auto flex min-h-[320px] max-w-[640px] flex-col items-center px-4 pt-6 sm:min-h-[380px] ${e.heroEdge === "fade" ? "pb-24" : "pb-10"}`}>
        {e.logoUrl ? (
          <img src={e.logoUrl} alt={e.heroTop || e.host} className="max-h-16 w-auto max-w-[70%]" />
        ) : (
          e.heroTop && (
            <p className="text-4xl tracking-wide" style={{ fontFamily: "var(--ev-heading-font)" }}>
              {e.heroTop}
            </p>
          )
        )}
        {e.cityLine && <p className="mt-1 text-xs tracking-[0.2em] uppercase opacity-90">{e.cityLine}</p>}
        <div className="flex flex-1 flex-col justify-end pt-10">
          {e.headline && (
            <h1 className="text-4xl leading-tight sm:text-5xl" style={{ fontFamily: "var(--ev-heading-font)" }}>
              {e.headline}
            </h1>
          )}
          {e.intro && <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed whitespace-pre-line opacity-90">{e.intro}</p>}
        </div>
      </div>
    </header>
  );
}

/** Where the hero meets the page, and above the footer: the pattern strip, or a line in a color (none when the hero fades). */
function Edge({ e, footer }: { e: EventPageView; footer?: boolean }) {
  if (e.heroEdge === "pattern" && e.bandUrl)
    return <div aria-hidden className="h-[18px] w-full" style={{ backgroundImage: `url(${JSON.stringify(e.bandUrl)})`, backgroundRepeat: "repeat-x", backgroundSize: "auto 100%" }} />;
  if (e.heroEdge === "fade" && !footer) return null;
  return <div aria-hidden className={footer && e.heroEdge === "fade" ? "h-px w-full opacity-20" : "h-[6px] w-full"} style={{ backgroundColor: e.edgeColor ?? "var(--ev-heading)" }} />;
}

function Section({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-[22px]" style={{ fontFamily: "var(--ev-heading-font)", color: "var(--ev-heading)" }}>
        {title}
      </h2>
      {note && <p className="mb-3 text-xs opacity-80">{note}</p>}
      <div className={note ? "" : "mt-2"}>{children}</div>
    </section>
  );
}

const card = "rounded-[4px] border border-black/10 bg-[var(--ev-card)]";
const input = `${card} w-full px-3 py-2.5 text-sm outline-none placeholder:opacity-50 focus:border-black/40`;

function Label({ children, optional }: { children: ReactNode; optional?: boolean }) {
  return (
    <span className="mb-1 block text-xs font-semibold">
      {children}
      {optional && <span className="font-normal opacity-60"> (optional)</span>}
    </span>
  );
}

function Radio({ on }: { on: boolean }) {
  return (
    <span aria-hidden className="flex size-[18px] shrink-0 items-center justify-center rounded-full border border-black/30">
      {on && <span className="size-[10px] rounded-full bg-[var(--ev-button)]" />}
    </span>
  );
}

/** The value once it has stopped changing for `ms` (so a lookup isn't made on every key). */
function useSettled<T>(value: T, ms: number): T {
  const key = JSON.stringify(value);
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [key, ms]); // eslint-disable-line react-hooks/exhaustive-deps
  return settled;
}

type Person = { firstName: string; lastName: string; dob: string; mobile: string; email: string; line1: string; line2: string; city: string; state: string; postalCode: string };

function BookingForm({ e, base }: { e: EventPageView; base: string }) {
  const [dateId, setDateId] = useState<string | null>(e.dates.length === 1 ? e.dates[0]!.id : null);
  const [dripId, setDripId] = useState<string | null>(null);
  const [extraId, setExtraId] = useState<string | null>(null);
  const [addOnIds, setAddOnIds] = useState<string[]>([]);
  const [time, setTime] = useState("");
  const [p, setP] = useState<Person>({ firstName: "", lastName: "", dob: "", mobile: "", email: "", line1: "", line2: "", city: "", state: "", postalCode: "" });
  const [texts, setTexts] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [forms, setForms] = useState(false);
  const [done, setDone] = useState<(EventPageConfirmation & { statusKey: string }) | null>(null);
  const set = (patch: Partial<Person>) => setP({ ...p, ...patch });
  // "Use different details": type everything, as for someone new.
  const [different, setDifferent] = useState(false);

  const date = e.dates.find((d) => d.id === dateId) ?? null;
  const drip = e.drips.find((d) => d.id === dripId) ?? null;
  // Add-ons (injections too) only go with a drip: the ones that fit the drip picked.
  const offered = drip ? e.addOns.filter((a) => drip.addOnIds.includes(a.id)) : [];
  const chosen = !!dripId || !!extraId;
  const picked = addOnIds.filter((id) => offered.some((a) => a.id === id));
  const groups = useMemo(() => [...new Set(offered.map((a) => a.group))].map((g) => ({ g, items: offered.filter((a) => a.group === g) })), [offered]);
  const slots = useQuery({
    queryKey: ["event-slots", base, dateId, dripId, extraId, picked.join(",")],
    queryFn: () => post<EventPageSlots>(`${base}/slots`, { occurrenceId: dateId, serviceId: dripId, extraId, addOnIds: picked }),
    enabled: !!dateId && chosen,
  });
  useEffect(() => {
    if (time && slots.data && !slots.data.times.includes(time)) setTime("");
  }, [slots.data, time]);

  const dob = isoFromTyped(p.dob);
  const minor = !!dob && ageOn(dob, todayLocal()) < 18;
  // Name and birthday typed (and settled for a moment): a client we know? Then their contact boxes
  // fold away and what's on file is used; nothing on file is ever shown here.
  const who = useSettled(dob && p.firstName.trim() && p.lastName.trim() ? { firstName: p.firstName.trim(), lastName: p.lastName.trim(), dob } : null, 700);
  const known = useQuery({ queryKey: ["event-known", base, who], queryFn: () => post<EventPageKnown>(`${base}/known`, who), enabled: !!who, staleTime: Infinity, retry: false });
  const onFile = !!who && known.data?.known === true && !different ? known.data : null;
  const ask = { mobile: !onFile || onFile.needs.mobile, email: !onFile || onFile.needs.email, address: !onFile || onFile.needs.address };
  const lookup: AddressLookup = useMemo(
    () => ({ suggest: (q) => post(`${base}/address/suggest`, { q }), resolve: (id) => post<ResolvedAddress>(`${base}/address/resolve`, { id }) }),
    [base],
  );
  const book = useMutation({
    mutationFn: () =>
      post<EventPageConfirmation & { statusKey: string }>(`${base}/bookings`, {
        tracking: trackingCookies(e?.tracking),
        occurrenceId: dateId,
        start: `${date!.start.slice(0, 10)}T${time}`,
        serviceId: dripId,
        extraId,
        addOnIds: picked,
        person: {
          firstName: p.firstName,
          lastName: p.lastName,
          dob: dob ?? p.dob,
          useOnFile: !!onFile,
          mobile: ask.mobile ? p.mobile : "",
          email: ask.email ? p.email : "",
          address: ask.address ? { line1: p.line1, line2: p.line2, city: p.city, state: p.state, postalCode: p.postalCode } : undefined,
        },
        textConsent: texts,
        depositAgreed: !noDeposit && agreed,
        formsAgreed: forms,
      }),
    onSuccess: (c) => {
      setDone(c);
      window.scrollTo({ top: 0 });
    },
  });

  const deposit = slots.data?.depositCents ?? drip?.depositCents ?? null;
  // Nothing to pay today: no deposit wording anywhere.
  const noDeposit = deposit != null ? deposit === 0 : e.drips.length > 0 && e.drips.every((d) => d.depositCents === 0);
  if (done) return <Booked e={e} base={base} c={done} />;
  const missing = !dateId ? "Pick a date." : !chosen ? (e.extras.length ? "Choose a drip or a recovery option." : "Choose your drip.") : !time ? "Pick a time." : !p.firstName || !p.lastName ? "Enter your name." : !dob ? "Enter your date of birth." : (ask.mobile && !p.mobile) || (ask.email && !p.email) ? (ask.mobile && ask.email ? "Enter your mobile number and email." : ask.mobile ? "Enter your mobile number." : "Enter your email.") : ask.address && (!p.line1 || !p.city || !p.state || !p.postalCode) ? "Enter your home address." : !texts || !forms || (!noDeposit && !agreed) ? "Tick the boxes above." : null;

  return (
    <form
      onSubmit={(ev) => {
        ev.preventDefault();
        if (!missing) book.mutate();
      }}
    >
      <Section title={e.dates.length === 1 ? "Your date" : "Which date?"}>
        <div className="flex flex-col gap-2">
          {e.dates.map((d) => {
            const m = monthDay(d.start);
            return (
              <button key={d.id} type="button" onClick={() => setDateId(d.id)} className={`${card} flex items-center gap-4 px-3 py-2.5 text-left ${dateId === d.id ? "border-[var(--ev-button)]" : ""}`} aria-pressed={dateId === d.id}>
                <span className="w-9 border-r border-black/10 pr-3 text-center leading-none">
                  <span className="block text-[10px] opacity-60">{m.month}</span>
                  <span className="block text-2xl" style={{ fontFamily: "var(--ev-heading-font)" }}>
                    {m.day}
                  </span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{d.title || `${time12(d.start)}–${time12(d.end)}`}</span>
                  <span className="block text-[11px] opacity-70">{d.subtitle || m.weekday}</span>
                </span>
                <Radio on={dateId === d.id} />
              </button>
            );
          })}
        </div>
      </Section>

      {e.drips.length > 0 && (
      <Section title="Choose your drip" note={e.extras.length ? `Optional if you're booking ${e.extrasTitle.toLowerCase()} only.${noDeposit ? "" : " Today's deposit holds your spot."}` : noDeposit ? undefined : "Today's deposit holds your spot. The rest is due at your appointment."}>
        <div className="flex flex-col gap-2">
          {e.drips.map((d) => (
            // With recovery on the menu the drip is optional: tapping it again takes it off.
            <button key={d.id} type="button" onClick={() => setDripId(dripId === d.id && e.extras.length ? null : d.id)} className={`${card} flex items-center gap-3 px-3 py-3 text-left ${dripId === d.id ? "border-[var(--ev-button)]" : ""}`} aria-pressed={dripId === d.id}>
              <Radio on={dripId === d.id} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">{d.name}</span>
                {d.description && <span className="block text-[11px] opacity-70">{d.description}</span>}
              </span>
              <span className="text-right tabular-nums">
                <Price cents={d.priceCents} regular={d.regularCents} />
                {d.depositCents > 0 && <span className="block text-[11px] opacity-70">{money(d.depositCents)} deposit</span>}
              </span>
            </button>
          ))}
        </div>
      </Section>
      )}

      {e.extras.length > 0 && (
        <Section title={e.extrasTitle} note={e.drips.length ? "With your drip, or on its own. Tap again to take it off." : undefined}>
          <div className="flex flex-col gap-2">
            {e.extras.map((x) => (
              <button key={x.id} type="button" onClick={() => setExtraId(extraId === x.id ? null : x.id)} className={`${card} flex items-center gap-3 px-3 py-3 text-left ${extraId === x.id ? "border-[var(--ev-button)]" : ""}`} aria-pressed={extraId === x.id}>
                <Radio on={extraId === x.id} />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{x.name}</span>
                  {x.description && <span className="block text-[11px] opacity-70">{x.description}</span>}
                </span>
                <span className="text-right tabular-nums">
                  <Price cents={x.priceCents} regular={x.regularCents} />
                </span>
              </button>
            ))}
          </div>
        </Section>
      )}

      {e.addOns.length > 0 && !drip && e.drips.length > 0 && (
        <Section title="Add a boost">
          <p className="text-sm opacity-70">Boosts and shots go with a drip. Choose a drip above to add them.</p>
        </Section>
      )}
      {offered.length > 0 && (
        <Section title="Add a boost" note={e.addOnsNote || "Optional."}>
          {groups.map(({ g, items }) => (
            <div key={g} className="mb-3">
              <p className="mb-2 text-xs font-semibold">{g}</p>
              <div className="flex flex-wrap gap-2">
                {items.map((a) => {
                  const on = picked.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setAddOnIds(on ? addOnIds.filter((x) => x !== a.id) : [...addOnIds, a.id])}
                      className={`rounded-full border px-3 py-1.5 text-xs ${on ? "border-[var(--ev-button)] bg-[var(--ev-button)] text-white" : "border-black/15 bg-[var(--ev-card)]"}`}
                    >
                      {a.name}
                      {a.note && <span className={on ? "opacity-80" : "opacity-60"}> {a.note}</span>}
                      {a.priceCents < a.regularCents && (
                        <span className={on ? "opacity-80" : "opacity-60"}>
                          {" "}
                          <s>{money(a.regularCents)}</s> {money(a.priceCents)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </Section>
      )}

      <Section title="Requested time">
        <label className="block">
          <Label>Time</Label>
          <select className={input} value={time} onChange={(ev) => setTime(ev.target.value)} disabled={!slots.data?.times.length}>
            <option value="">{!dateId || !chosen ? (e.extras.length ? "Pick a date and what you'd like first" : "Pick a date and drip first") : slots.isLoading ? "Finding open times…" : slots.data?.times.length ? "Select a time" : "No open times left on this date"}</option>
            {slots.data?.times.map((t) => (
              <option key={t} value={t}>
                {time12(`2000-01-01T${t}`)}
              </option>
            ))}
          </select>
        </label>
        {slots.error && <p className="mt-2 text-sm text-red-700">{errorMessage(slots.error)}</p>}
      </Section>

      <Section title="About you">
        <div className="grid grid-cols-2 gap-3">
          <label>
            <Label>First name</Label>
            <input className={input} autoComplete="given-name" value={p.firstName} onChange={(ev) => set({ firstName: ev.target.value })} />
          </label>
          <label>
            <Label>Last name</Label>
            <input className={input} autoComplete="family-name" value={p.lastName} onChange={(ev) => set({ lastName: ev.target.value })} />
          </label>
          <label className="col-span-2">
            <Label>Date of birth</Label>
            <input className={input} inputMode="numeric" autoComplete="bday" placeholder="MM/DD/YYYY" value={p.dob} onChange={(ev) => set({ dob: maskDob(ev.target.value) })} />
          </label>
          {onFile && (
            <div className={`${card} col-span-2 px-3 py-2.5 text-sm`}>
              <p className="font-semibold">Welcome back, {p.firstName.trim()}.</p>
              <p className="mt-0.5 opacity-80">
                {onFile.mobileLast4 && !onFile.needs.mobile ? `We'll text the mobile ending ${onFile.mobileLast4} and use the details we have on file.` : "We'll use the details we have on file."}
                {(onFile.needs.mobile || onFile.needs.email || onFile.needs.address) && " Just fill in what's missing below."}
              </p>
              <button type="button" className="mt-1 text-xs font-semibold underline" onClick={() => setDifferent(true)}>
                Use different details
              </button>
            </div>
          )}
          {ask.mobile && (
            <label className="col-span-2">
              <Label>Mobile number</Label>
              <input className={input} type="tel" autoComplete="tel" placeholder="(405) 555-0123" value={p.mobile} onChange={(ev) => set({ mobile: maskPhone(ev.target.value) })} />
            </label>
          )}
          {ask.email && (
            <label className="col-span-2">
              <Label>Email</Label>
              <input className={input} type="email" autoComplete="email" value={p.email} onChange={(ev) => set({ email: ev.target.value })} />
            </label>
          )}
        </div>
        {minor && <p className={`${card} mt-3 px-3 py-2.5 text-sm font-semibold`}>{MINOR_NOTICE}</p>}
      </Section>

      {ask.address && (
        <Section title="Home address">
          <div className="grid grid-cols-[1fr_72px_96px] gap-3">
            <div className="col-span-3">
              <AddressField
                label="Street address"
                labelClassName="mb-1 block text-xs font-semibold"
                inputClassName={input}
                placeholder="Start typing your address"
                value={p.line1}
                lookup={lookup}
                onChange={(line1) => set({ line1 })}
                onResolved={(r) => set({ line1: r.line1, city: r.city, state: r.state, postalCode: r.postalCode, ...(r.line2 ? { line2: r.line2 } : {}) })}
              />
            </div>
            <label className="col-span-3">
              <Label optional>Apt or unit</Label>
              <input className={input} autoComplete="address-line2" value={p.line2} onChange={(ev) => set({ line2: ev.target.value })} />
            </label>
            <label>
              <Label>City</Label>
              <input className={input} autoComplete="address-level2" value={p.city} onChange={(ev) => set({ city: ev.target.value })} />
            </label>
            <label>
              <Label>State</Label>
              <input className={input} autoComplete="address-level1" maxLength={40} value={p.state} onChange={(ev) => set({ state: ev.target.value })} />
            </label>
            <label>
              <Label>ZIP</Label>
              <input className={input} inputMode="numeric" autoComplete="postal-code" value={p.postalCode} onChange={(ev) => set({ postalCode: ev.target.value })} />
            </label>
          </div>
        </Section>
      )}

      <Section title={noDeposit ? "Confirm" : "Pay your deposit"}>
        {!noDeposit && (
          <div className="mb-3 rounded-[4px] border border-black/10 bg-white px-3 py-3 text-sm">
            {!chosen ? (
              e.extras.length ? "Choose a drip or a recovery option to see your deposit." : "Choose a drip to see your deposit."
            ) : (
              <>
                <span className="flex justify-between gap-3 tabular-nums">
                  <span className="font-semibold">Due today (deposit)</span>
                  <span className="font-semibold">{deposit != null ? money(deposit) : "…"}</span>
                </span>
                {slots.data && deposit != null && (
                  <span className="mt-0.5 block text-xs tabular-nums opacity-70">
                    Total {money(slots.data.totalCents)} · {money(Math.max(0, slots.data.totalCents - deposit))} due at your appointment
                  </span>
                )}
              </>
            )}
          </div>
        )}
        <Tick checked={texts} onChange={setTexts}>
          {eventPageTextConsent(e.clinicName)}
        </Tick>
        {!noDeposit && (
          <Tick checked={agreed} onChange={setAgreed}>
            {deposit != null ? eventPageDepositAck(deposit) : "I understand the deposit is non-refundable and goes toward my total."}
          </Tick>
        )}
        <Tick checked={forms} onChange={setForms}>
          {EVENT_PAGE_FORMS_ACK}
        </Tick>
        {book.error && <p className="my-2 rounded-[4px] bg-red-50 px-3 py-2 text-sm text-red-800">{errorMessage(book.error)}</p>}
        <button type="submit" disabled={book.isPending} className="mt-3 w-full rounded-[4px] bg-[var(--ev-button)] px-4 py-3 text-base font-semibold text-white disabled:opacity-60">
          {book.isPending ? "Holding your spot…" : deposit ? `Book and pay ${money(deposit)} deposit` : "Book"}
        </button>
        {missing && <p className="mt-2 text-center text-xs opacity-70">{missing}</p>}
        {!noDeposit && <p className="mt-3 text-center text-xs opacity-70">Apple Pay, Google Pay, and credit or debit cards accepted.</p>}
      </Section>
    </form>
  );
}

/** The event's price, with the usual one struck through when it's lower. */
function Price({ cents, regular }: { cents: number; regular: number }) {
  return (
    <span className="block text-sm font-semibold">
      {cents < regular && <s className="mr-1.5 text-xs font-normal opacity-60">{money(regular)}</s>}
      {money(cents)}
    </span>
  );
}

function Tick({ checked, onChange, children }: { checked: boolean; onChange: (on: boolean) => void; children: ReactNode }) {
  return (
    <label className="mb-2 flex items-start gap-2 text-[11px] leading-snug opacity-90">
      <input type="checkbox" className="mt-0.5 size-4 shrink-0 accent-[var(--ev-button)]" checked={checked} onChange={(ev) => onChange(ev.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

/** After "Book": pay the deposit (Stripe, embedded), then the confirmation and forms link. */
function Booked({ e, base, c }: { e: EventPageView; base: string; c: EventPageConfirmation & { statusKey: string } }) {
  const [finished, setFinished] = useState(!c.payment);
  const status = useQuery({
    queryKey: ["event-booking-status", c.reference],
    queryFn: () => get<{ status: string; depositStatus: string; forms: { appointmentId: string; formsNeeded: number; screening: boolean }[] }>(`${base}/bookings/${c.reference}/status?k=${c.statusKey}`),
    enabled: finished,
    refetchInterval: (q) => (q.state.data && q.state.data.status !== "pending" ? 30_000 : 1500),
    refetchOnWindowFocus: true,
  });
  const s = status.data;
  const g = c.guests[0]!;
  const date = e.dates.find((d) => c.start.startsWith(d.start.slice(0, 10)));
  if (!finished && c.payment) {
    return (
      <Section title={`Pay your ${money(c.payment.amountCents)} deposit`} note="Your time is held for 30 minutes while you pay.">
        <StripeCheckout session={c.payment.session} onComplete={() => setFinished(true)} />
      </Section>
    );
  }
  if (s?.status === "expired") return <p className="py-10 text-center">This booking timed out before the deposit was paid. Please book again.</p>;
  if (!s || s.status === "pending")
    return (
      <p className="flex items-center justify-center gap-3 py-10">
        <span className="size-4 animate-spin rounded-full border-2 border-[var(--ev-button)] border-r-transparent" aria-hidden /> Finishing your booking…
      </p>
    );
  const formsLeft = s.forms[0]?.formsNeeded ?? g.formsNeeded;
  return (
    <div className="flex flex-col gap-4 py-4">
      <h2 className="text-3xl" style={{ fontFamily: "var(--ev-heading-font)", color: "var(--ev-heading)" }}>
        You're booked, {g.name}.
      </h2>
      <div className={`${card} px-4 py-3 text-sm`}>
        {date?.title && <p className="font-semibold">{date.title}</p>}
        <p>
          {new Date(`${c.start.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" })} at {time12(c.start)}
        </p>
        <p className="mt-1">
          {g.serviceName}
          {g.addOns.length > 0 && ` + ${g.addOns.join(", ")}`}
        </p>
        {c.depositCents != null && (
          <p className="mt-1 opacity-80">
            Deposit paid: {money(c.depositCents)}. Due at your appointment: {money(Math.max(0, c.totalCents - c.depositCents))}.
          </p>
        )}
        <p className="mt-1 text-xs opacity-70">Reference {c.reference}</p>
      </div>
      {c.minor && <p className={`${card} px-4 py-3 text-sm font-semibold`}>{MINOR_NOTICE}</p>}
      {formsLeft > 0 ? (
        <div className={`${card} px-4 py-3 text-sm`}>
          <p className="font-semibold">Next: your intake form</p>
          <p className="mt-1 opacity-80">
            It confirms your appointment{g.screening ? ", followed by a quick health screening" : ""}. {g.formsLink ? "We've texted you the link too." : "We've texted the link to your mobile."}
          </p>
          {g.formsLink && (
            <a href={g.formsLink} target="_blank" rel="noreferrer" className="mt-3 inline-block rounded-[4px] bg-[var(--ev-button)] px-4 py-2.5 font-semibold text-white">
              Fill out my intake form
            </a>
          )}
        </div>
      ) : (
        <p className={`${card} px-4 py-3 text-sm font-semibold`}>Forms done. You're all set for your visit.</p>
      )}
    </div>
  );
}
